/* ============================================================================
   CARROUSEL PUBLICITAIRE
   ----------------------------------------------------------------------------
   Encarts discrets (bandeau ~90 px, étiquette « Publicité » obligatoire) qui
   tournent automatiquement. AUCUN script tiers ni traceur : de simples images
   cliquables.
   Les annonces vivent dans la table Supabase `ads` et se gèrent depuis le
   Dashboard admin (ajout, lien, activation, suppression) — aucun redéploiement
   nécessaire. Format conseillé : 728×90 px (ratio ~8:1), PNG/JPG/WebP/SVG,
   < 200 Ko.
   ========================================================================== */
import { supabase } from "./supabaseClient.js";

const ROTATE_MS = 7000;   // délai entre deux annonces

/* Mesure d'audience (agrégée, sans traceur). Une impression par annonce et par
   session (anti-inflation) ; un clic à chaque ouverture du lien. Fire-and-forget. */
const seenImpressions = new Set();
function registerImpression(id) {
  if (!id || seenImpressions.has(id)) return;
  seenImpressions.add(id);
  supabase.rpc("register_ad_impression", { p_ad: id }).then(() => {}, () => {});
}
function registerClick(id) {
  if (!id) return;
  supabase.rpc("register_ad_click", { p_ad: id }).then(() => {}, () => {});
}

/* Annonces actives, ordonnées. En cas d'erreur (table absente, réseau) on
   renvoie une liste vide : les emplacements se masquent d'eux-mêmes. */
export async function loadAds() {
  try {
    const { data, error } = await supabase
      .from("ads")
      .select("id,image_url,href,alt")
      .eq("active", true)
      .order("position", { ascending: true });
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.warn("[ads] chargement impossible :", err?.message || err);
    return [];
  }
}

/* Monte un carrousel dans le conteneur donné. Sans annonce, le bloc disparaît.
   Rotation automatique en fondu, mise en pause au survol, points de position. */
export function mountAdCarousel(container, ads) {
  if (!container) return;
  if (!ads || !ads.length) { container.hidden = true; return; }
  container.hidden = false;

  container.classList.add("ad-card");
  container.innerHTML = `
    <span class="ad-tag" title="Encarts maison : nos autres applications — image simple, sans traceur">Découvrez nos autres applications</span>
    <a class="ad-link" target="_blank" rel="noopener nofollow sponsored">
      <img class="ad-img" alt="">
    </a>
    ${ads.length > 1 ? `<div class="ad-dots">${ads.map((_, i) => `<button data-i="${i}" aria-label="Annonce ${i + 1}"></button>`).join("")}</div>` : ""}
  `;

  const link = container.querySelector(".ad-link");
  const img = container.querySelector(".ad-img");
  const dots = Array.from(container.querySelectorAll(".ad-dots button"));
  let index = 0;
  let timer = null;

  function show(i) {
    index = (i + ads.length) % ads.length;
    const ad = ads[index];
    registerImpression(ad.id);
    img.classList.add("is-fading");
    setTimeout(() => {
      img.src = ad.image_url;
      img.alt = ad.alt || "";
      link.href = ad.href;
      img.classList.remove("is-fading");
    }, 180);
    dots.forEach((d, j) => d.classList.toggle("is-active", j === index));
  }
  function start() { if (ads.length > 1 && !timer) timer = setInterval(() => show(index + 1), ROTATE_MS); }
  function stop() { clearInterval(timer); timer = null; }

  dots.forEach((d) => d.addEventListener("click", () => { stop(); show(+d.dataset.i); start(); }));
  link.addEventListener("click", () => registerClick(ads[index].id));
  container.addEventListener("mouseenter", stop);
  container.addEventListener("mouseleave", start);

  // Premier affichage sans fondu.
  const first = ads[0];
  img.src = first.image_url; img.alt = first.alt || ""; link.href = first.href;
  registerImpression(first.id);
  dots.forEach((d, j) => d.classList.toggle("is-active", j === 0));
  start();
}
